import { useEffect, useRef, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../Store/store';
import { getTaskStatus, generateCourse, generateIntermediateCourse, generateAdvancedCourse } from '../Services/course.service';

export const useCoursePolling = () => {
  const dispatch = useAppDispatch();
  const userId = useAppSelector(state => state.user.uid);
  const user = useAppSelector(state => state.user.user);
  const taskId = useAppSelector(state => state.course?.task_id);
  const intermediateTaskId = useAppSelector(state => state.course?.intermediate_task_id);
  const advancedTaskId = useAppSelector(state => state.course?.advance_task_id);
  const course = useAppSelector(state => state.course?.course);
  const intermediateCourse = useAppSelector(state => state.course?.intermediateCourse);
  const advancedCourse = useAppSelector(state => state.course?.advancedCourse);
  
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const intermediatePollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const advancedPollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Use useCallback to prevent unnecessary re-renders and dependency issues
  const handleGenerateCourse = useCallback(async () => {
    if (user.remaining_tokens >= 1 && user?.test_given === true) {
      try {
        const res = await dispatch(generateCourse(userId)).unwrap();
        return res;
      } catch (error) {
        console.error("Error generating course:", error);
        throw error;
      }
    } else {
      console.error("User not eligible for course generation");
      return null;
    }
  }, [dispatch, userId, user?.is_paid, user?.test_given]);

  const handleGenerateIntermediateCourse = useCallback(async () => {
    try {
      const res = await dispatch(generateIntermediateCourse(userId)).unwrap();
      return res;
    } catch (error) {
      console.error("Error generating intermediate course:", error);
      throw error;
    }
  }, [dispatch, userId]);

  const handleGenerateAdvancedCourse = useCallback(async () => {
    try {
      const res = await dispatch(generateAdvancedCourse(userId)).unwrap();
      return res;
    } catch (error) {
      console.error("Error generating advanced course:", error);
      throw error;
    }
  }, [dispatch, userId]);

  const pollTaskStatus = useCallback(async (currentTaskId: string) => {
    if (!currentTaskId) {
      console.warn("No task ID provided for polling");
      return null;
    }

    try {
      const response = await dispatch(getTaskStatus(currentTaskId)).unwrap();      
      if (response?.result !== null && response?.status !== "PENDING") {
        // Course is ready, stop polling
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }        
        // Generate intermediate course after main course is completed
        // if (!intermediateTaskId && !intermediateCourse) {
        //   await handleGenerateIntermediateCourse();
        // }
        
      } else if (response?.status === "FAILURE") {
        // Task failed, stop polling
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        console.error("Course generation failed");
        
      } else {
        // Still pending, continue polling
        console.error("Course generation in progress...", response?.status);
      }
      
      return response;
    } catch (error) {
      console.error("Error checking task status:", error);
      // Don't throw here to prevent stopping the polling
      return null;
    }
  }, [dispatch, intermediateTaskId, intermediateCourse, handleGenerateIntermediateCourse]);

  const pollIntermediateTaskStatus = useCallback(async (currentTaskId: string) => {
    if (!currentTaskId) {
      console.warn("No intermediate task ID provided for polling");
      return null;
    }

    try {
      const response = await dispatch(getTaskStatus(currentTaskId)).unwrap();
      
      if (response?.result !== null && response?.status !== "PENDING") {
        // Intermediate course is ready, stop polling
        if (intermediatePollingIntervalRef.current) {
          clearInterval(intermediatePollingIntervalRef.current);
          intermediatePollingIntervalRef.current = null;
        }
        
        // Generate advanced course after intermediate course is completed
        if (!advancedTaskId && !advancedCourse) {
          await handleGenerateAdvancedCourse();
        }
        
      } else if (response?.status === "FAILURE") {
        // Task failed, stop polling
        if (intermediatePollingIntervalRef.current) {
          clearInterval(intermediatePollingIntervalRef.current);
          intermediatePollingIntervalRef.current = null;
        }
        console.error("Intermediate course generation failed");
        
      } else {
        // Still pending, continue polling
        console.error("Intermediate course generation in progress...", response?.status);
      }
      
      return response;
    } catch (error) {
      console.error("Error checking intermediate task status:", error);
      return null;
    }
  }, [dispatch, advancedTaskId, advancedCourse, handleGenerateAdvancedCourse]);

  const pollAdvancedTaskStatus = useCallback(async (currentTaskId: string) => {
    if (!currentTaskId) {
      console.warn("No advanced task ID provided for polling");
      return null;
    }

    try {
      const response = await dispatch(getTaskStatus(currentTaskId)).unwrap();
      
      if (response?.result !== null && response?.status !== "PENDING") {
        // Advanced course is ready, stop polling
        if (advancedPollingIntervalRef.current) {
          clearInterval(advancedPollingIntervalRef.current);
          advancedPollingIntervalRef.current = null;
        }
        
      } else if (response?.status === "FAILURE") {
        // Task failed, stop polling
        if (advancedPollingIntervalRef.current) {
          clearInterval(advancedPollingIntervalRef.current);
          advancedPollingIntervalRef.current = null;
        }
        console.error("Advanced course generation failed");
        
      } else {
        // Still pending, continue polling
        console.error("Advanced course generation in progress...", response?.status);
      }
      
      return response;
    } catch (error) {
      console.error("Error checking advanced task status:", error);
      return null;
    }
  }, [dispatch]);

  // Start polling function for main course
  const startPolling = useCallback((currentTaskId: string) => {
    if (!currentTaskId) {
      console.warn("Cannot start polling: no task ID provided");
      return;
    }

    // Clear any existing polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
        
    // Do initial check immediately
    pollTaskStatus(currentTaskId);
    
    // Start polling every 30 seconds
    pollingIntervalRef.current = setInterval(() => {
      pollTaskStatus(currentTaskId);
    }, 30000);
  }, [pollTaskStatus]);

  // Start polling function for intermediate course
  const startIntermediatePolling = useCallback((currentTaskId: string) => {
    if (!currentTaskId) {
      console.warn("Cannot start intermediate polling: no task ID provided");
      return;
    }

    // Clear any existing polling
    if (intermediatePollingIntervalRef.current) {
      clearInterval(intermediatePollingIntervalRef.current);
    }
    
    // Do initial check immediately
    pollIntermediateTaskStatus(currentTaskId);
    
    // Start polling every 30 seconds
    intermediatePollingIntervalRef.current = setInterval(() => {
      pollIntermediateTaskStatus(currentTaskId);
    }, 30000);
  }, [pollIntermediateTaskStatus]);

  // Start polling function for advanced course
  const startAdvancedPolling = useCallback((currentTaskId: string) => {
    if (!currentTaskId) {
      console.warn("Cannot start advanced polling: no task ID provided");
      return;
    }

    // Clear any existing polling
    if (advancedPollingIntervalRef.current) {
      clearInterval(advancedPollingIntervalRef.current);
    }
    
    // Do initial check immediately
    pollAdvancedTaskStatus(currentTaskId);
    
    // Start polling every 30 seconds
    advancedPollingIntervalRef.current = setInterval(() => {
      pollAdvancedTaskStatus(currentTaskId);
    }, 30000);
  }, [pollAdvancedTaskStatus]);

  // Stop polling function for main course
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  // Stop polling function for intermediate course
  const stopIntermediatePolling = useCallback(() => {
    if (intermediatePollingIntervalRef.current) {
      clearInterval(intermediatePollingIntervalRef.current);
      intermediatePollingIntervalRef.current = null;
    }
  }, []);

  // Stop polling function for advanced course
  const stopAdvancedPolling = useCallback(() => {
    if (advancedPollingIntervalRef.current) {
      clearInterval(advancedPollingIntervalRef.current);
      advancedPollingIntervalRef.current = null;
    }
  }, []);

  // Effect to handle initial course generation
  // useEffect(() => {
  //   if (userId && user?.is_paid && user?.test_given && !course && !taskId) {
  //     handleGenerateCourse().catch(error => {
  //       console.error("Failed to initiate course generation:", error);
  //     });
  //   }
  // }, [userId, user?.is_paid, user?.test_given, course, taskId, handleGenerateCourse]);

  // Effect to handle polling when main taskId is available
  useEffect(() => {
    if (taskId && !course) {
      startPolling(taskId.toLocaleString());
    }
    
    // Cleanup on unmount or when taskId changes
    return () => {
      stopPolling();
    };
  }, [taskId, course, startPolling, stopPolling]);

  // Effect to handle polling when intermediate taskId is available
  // useEffect(() => {
  //   if (intermediateTaskId && !intermediateCourse) {
  //     startIntermediatePolling(intermediateTaskId.toLocaleString());  
  //   }
    
  //   // Cleanup on unmount or when intermediateTaskId changes
  //   return () => {
  //     stopIntermediatePolling();
  //   };
  // }, [intermediateTaskId, intermediateCourse, startIntermediatePolling, stopIntermediatePolling]);

  // Effect to handle polling when advanced taskId is available
  // useEffect(() => {
  //   if (advancedTaskId && !advancedCourse) {
  //     startAdvancedPolling(advancedTaskId.toLocaleString());  
  //   }
    
  //   // Cleanup on unmount or when advancedTaskId changes
  //   return () => {
  //     stopAdvancedPolling();
  //   };
  // }, [advancedTaskId, advancedCourse, startAdvancedPolling, stopAdvancedPolling]);

  // Cleanup intervals on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      if (intermediatePollingIntervalRef.current) {
        clearInterval(intermediatePollingIntervalRef.current);
      }
      if (advancedPollingIntervalRef.current) {
        clearInterval(advancedPollingIntervalRef.current);
      }
    };
  }, []);

  // Return values and functions that might be needed
  return {
    userId,
    taskId,
    intermediateTaskId,
    advancedTaskId,
    course,
    intermediateCourse,
    advancedCourse,
    isPolling: !!pollingIntervalRef.current,
    isIntermediatePolling: !!intermediatePollingIntervalRef.current,
    isAdvancedPolling: !!advancedPollingIntervalRef.current,
    handleGenerateCourse,
    handleGenerateIntermediateCourse,
    handleGenerateAdvancedCourse,
    stopPolling,
    stopIntermediatePolling,
    stopAdvancedPolling,
    startPolling,
    startIntermediatePolling,
    startAdvancedPolling
  };
};